import { useEffect, useRef } from 'react';
import type { AdvancedFilterQuery } from '@packages/types';
import type { ActiveFilter, FilterMetadata } from '../types/filter-metadata.types';
import { FilterInputType } from '../types/filter-metadata.types';
import type { ConditionOperator, FilterGroup, GroupOperator } from '../types/filter-panel.types';

interface UseEmitFiltersProps {
    filterGroups: FilterGroup[];
    metadata?: { filters: FilterMetadata[] };
    multiSelectInValues: Record<string, any[]>;
    multiSelectOutValues: Record<string, any[]>;
    multiSelectOperator: Record<string, GroupOperator>;
    onAdvancedFiltersChange?: (filters?: AdvancedFilterQuery) => void;
    onFiltersChange?: (filters: ActiveFilter[]) => void;
}

/**
 * Infers the operator for a condition based on field and value type
 */
const inferOperator = (
    field: string,
    value: any,
    isBooleanField: boolean,
    currentOperator?: ConditionOperator
): ConditionOperator => {
    if (currentOperator) return currentOperator;

    if (isBooleanField) {
        return value === true ? 'exists' : 'not_exists';
    }

    if (Array.isArray(value)) {
        return field === 'age' ? 'between' : 'in';
    }

    if (field === 'age') {
        return 'between';
    }

    return 'equals';
};

/**
 * Builds value statuses (IN/OUT) for multi-select fields
 */
const buildValueStatuses = (
    inValues: any[],
    outValues: any[]
): { allValues: any[]; valueStatuses: Record<string, 'IN' | 'OUT'>; valueTitleMap: Record<string, string> } => {
    const inValueArray = inValues.map((v: any) => (typeof v === 'object' && v.value !== undefined ? v.value : v));
    const outValueArray = outValues.map((v: any) => (typeof v === 'object' && v.value !== undefined ? v.value : v));
    const allValues = [...inValueArray, ...outValueArray];

    const valueStatuses: Record<string, 'IN' | 'OUT'> = {};
    inValueArray.forEach((val: any) => {
        valueStatuses[String(val)] = 'IN';
    });
    outValueArray.forEach((val: any) => {
        valueStatuses[String(val)] = 'OUT';
    });

    const valueTitleMap: Record<string, string> = {};
    [...inValues, ...outValues].forEach((item: any) => {
        const val = typeof item === 'object' && item.value !== undefined ? item.value : item;
        const title = typeof item === 'object' && item.title !== undefined ? item.title : String(val);
        if (title) {
            valueTitleMap[String(val)] = title;
        }
    });

    return { allValues, valueStatuses, valueTitleMap };
};

/**
 * Hook to debounce and emit filter changes
 */
export const useEmitFilters = ({
    filterGroups,
    metadata,
    multiSelectInValues,
    multiSelectOutValues,
    multiSelectOperator,
    onAdvancedFiltersChange,
    onFiltersChange,
}: UseEmitFiltersProps): void => {
    const emitTimerRef = useRef<number | null>(null);

    // Serialize values for dependency tracking (strings are compared by value)
    // Include measurementValueRanges in serialization to detect changes
    const filterGroupsSerialized = JSON.stringify(filterGroups, (key, value) => {
        // Ensure measurementValueRanges metadata is included in serialization
        return value;
    });
    const multiSelectOperatorSerialized = JSON.stringify(multiSelectOperator);
    const multiSelectInValuesSerialized = JSON.stringify(multiSelectInValues);
    const multiSelectOutValuesSerialized = JSON.stringify(multiSelectOutValues);

    useEffect(() => {
        const groups = filterGroups
            .map(g => {
                const conditions: any[] = [];

                g.conditions
                    .filter(c => {
                        // Include condition if it has a value
                        if (c.value !== undefined && c.value !== null && c.value !== '') {
                            if (Array.isArray(c.value) && c.value.length === 0) {
                                // Even if value array is empty, check if measurementValueRanges exists
                                if (c.field === 'measurement_type' && (c as any).measurementValueRanges) {
                                    const ranges = (c as any).measurementValueRanges;
                                    if (ranges && Object.keys(ranges).length > 0) {
                                        // Check if any range has min/max OR outstanding flag
                                        const hasValidRange = Object.values(ranges).some((range: any) => {
                                            if (!range) return false;
                                            // Include if has min/max OR outstanding is true (will use default ranges)
                                            return (
                                                range.min !== undefined ||
                                                range.max !== undefined ||
                                                range.outstanding === true
                                            );
                                        });
                                        if (hasValidRange) return true;
                                    }
                                }
                                return false;
                            }
                            return true;
                        }
                        // Include condition if it has measurementValueRanges metadata (even without value)
                        if (c.field === 'measurement_type' && (c as any).measurementValueRanges) {
                            const ranges = (c as any).measurementValueRanges;
                            if (ranges && Object.keys(ranges).length > 0) {
                                // Check if any range has min/max OR outstanding flag
                                const hasValidRange = Object.values(ranges).some((range: any) => {
                                    if (!range) return false;
                                    // Include if has min/max OR outstanding is true (will use default ranges)
                                    return (
                                        range.min !== undefined || range.max !== undefined || range.outstanding === true
                                    );
                                });
                                if (hasValidRange) return true;
                            }
                        }
                        // Include procedure_type if it has procedureTypeFilters metadata (even without value)
                        if (c.field === 'procedure_type' && (c as any).procedureTypeFilters) {
                            const filters = (c as any).procedureTypeFilters;
                            if (filters && typeof filters === 'object' && Object.keys(filters).length > 0) {
                                const hasAnyFilters = Object.values(filters).some((typeFilter: any) => {
                                    return (
                                        typeFilter &&
                                        (typeFilter.dateRange ||
                                            typeFilter.countMin !== undefined ||
                                            typeFilter.countMax !== undefined)
                                    );
                                });
                                if (hasAnyFilters) return true;
                            }
                        }
                        return false;
                    })
                    .forEach(c => {
                        const meta = metadata?.filters.find(f => f.field === c.field);
                        const isBooleanField = meta?.inputType === FilterInputType.BOOLEAN;
                        const isMultiSelect = meta?.inputType === FilterInputType.MULTI_SELECT;

                        const operator = inferOperator(c.field, c.value, isBooleanField, c.operator);
                        const conditionKey = `${g.id}-${c.id}`;

                        const baseCondition: any = {
                            field: c.field,
                            operator,
                            value: c.value,
                            conditionOperator: c.conditionOperator || 'AND',
                        };

                        // For MULTI_SELECT fields, add additional metadata
                        if (isMultiSelect) {
                            const inValues = multiSelectInValues[conditionKey] || [];
                            const outValues = multiSelectOutValues[conditionKey] || [];
                            const { allValues, valueStatuses, valueTitleMap } = buildValueStatuses(inValues, outValues);

                            if (allValues.length > 0) {
                                baseCondition.value = allValues;
                                baseCondition.logicalOperator = multiSelectOperator[conditionKey] || 'OR';
                                baseCondition.valueStatuses = valueStatuses;
                                if (Object.keys(valueTitleMap).length > 0) {
                                    baseCondition.valueTitleMap = valueTitleMap;
                                }
                            }
                        }

                        // For measurement_value field, add measurementType metadata if present
                        if (c.field === 'measurement_value' && (c as any).measurementType) {
                            baseCondition.measurementType = (c as any).measurementType;
                        }

                        // For measurement_type field, include measurementValueRanges metadata if present
                        // Backend will process this metadata to filter by value ranges
                        if (c.field === 'measurement_type' && (c as any).measurementValueRanges) {
                            const ranges = (c as any).measurementValueRanges;
                            // Include if there are valid ranges OR outstanding/normalOnly flags (can use default ranges)
                            if (ranges && Object.keys(ranges).length > 0) {
                                const hasValidRange = Object.values(ranges).some((range: any) => {
                                    if (!range) return false;
                                    // Include if has min/max OR outstanding/normalOnly is true (will use default ranges)
                                    return (
                                        range.min !== undefined ||
                                        range.max !== undefined ||
                                        range.outstanding === true ||
                                        range.normalOnly === true
                                    );
                                });
                                if (hasValidRange) {
                                    baseCondition.measurementValueRanges = ranges;
                                }
                            }
                        }

                        // For note_type field, include noteTypeFilters metadata if present
                        // Backend will process this to filter notes by date range and count
                        // New structure: { "Radiology": { dateRange, countMin, countMax }, ... }
                        if (c.field === 'note_type' && (c as any).noteTypeFilters) {
                            const filters = (c as any).noteTypeFilters;
                            // Check if any type has filters defined
                            if (filters && typeof filters === 'object') {
                                const hasAnyFilters = Object.values(filters).some((typeFilter: any) => {
                                    return (
                                        typeFilter &&
                                        (typeFilter.dateRange ||
                                            typeFilter.countMin !== undefined ||
                                            typeFilter.countMax !== undefined)
                                    );
                                });
                                if (hasAnyFilters) {
                                    baseCondition.noteTypeFilters = filters;
                                }
                            }
                        }

                        // For procedure_type field, include procedureTypeFilters metadata if present
                        // Backend will process this to filter procedures by date range and count
                        // New structure: { "CT of head": { dateRange, countMin, countMax }, ... }
                        if (c.field === 'procedure_type' && (c as any).procedureTypeFilters) {
                            const filters = (c as any).procedureTypeFilters;
                            // Check if any type has filters defined
                            if (filters && typeof filters === 'object') {
                                const hasAnyFilters = Object.values(filters).some((typeFilter: any) => {
                                    return (
                                        typeFilter &&
                                        (typeFilter.dateRange ||
                                            typeFilter.countMin !== undefined ||
                                            typeFilter.countMax !== undefined)
                                    );
                                });
                                if (hasAnyFilters) {
                                    baseCondition.procedureTypeFilters = filters;
                                    // Ensure value includes procedure types from filters keys (for ES+OMOP etc.)
                                    if (
                                        !baseCondition.value ||
                                        (Array.isArray(baseCondition.value) && baseCondition.value.length === 0)
                                    ) {
                                        baseCondition.value = Object.keys(filters);
                                    }
                                }
                            }
                        }

                        conditions.push(baseCondition);
                    });

                return {
                    operator: g.operator,
                    conditions,
                };
            })
            .filter(g => g.conditions.length > 0);

        const advanced: AdvancedFilterQuery | undefined = groups.length > 0 ? { groups: groups as any } : undefined;

        // Debounce emitting to avoid excessive requests while the user is sliding/typing
        if (emitTimerRef.current) {
            window.clearTimeout(emitTimerRef.current);
        }

        emitTimerRef.current = window.setTimeout(() => {
            onAdvancedFiltersChange?.(advanced);

            // Back-compat: also emit flattened ActiveFilter[] if requested
            if (onFiltersChange) {
                const flattened: ActiveFilter[] = [];
                (advanced?.groups || []).forEach(g => {
                    g.conditions.forEach(c => {
                        flattened.push({
                            field: c.field,
                            operator: c.operator as any,
                            value: c.value,
                        });
                    });
                });
                onFiltersChange(flattened);
            }
        }, 350);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        filterGroupsSerialized,
        multiSelectOperatorSerialized,
        multiSelectInValuesSerialized,
        multiSelectOutValuesSerialized,
    ]);
};
